import { Component, useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { toast } from "sonner";

const FALLBACK_IMG = "data:image/svg+xml;utf8," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 300"><rect width="200" height="300" fill="#0B0B0D"/><path d="M70 170l15-40 15 25 15-25 15 40z" fill="none" stroke="#D4AF37" stroke-opacity=".45" stroke-width="4"/></svg>');

export class RouteErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("AETRYX route error", error, info?.componentStack); } // eslint-disable-line no-console
  componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null }); }
  render() {
    if (!this.state.error) return this.props.children;
    const chunk = /Loading chunk|ChunkLoadError|dynamically imported module/i.test(String(this.state.error?.message || this.state.error));
    return (
      <div data-testid="route-error" className="grid min-h-[70vh] place-items-center bg-[#050505] px-6 text-center text-white">
        <div className="max-w-md">
          <p className="eyebrow">Something went wrong</p>
          <h1 className="mt-3 font-display text-3xl gold-text">This screen hit a snag</h1>
          <p className="mt-3 text-sm text-white/60">{chunk ? "A newer version of AETRYX is available. Reload to continue." : "Your data is safe. Try again — if it keeps happening, contact support."}</p>
          <div className="mt-6 flex justify-center gap-3">
            <button data-testid="route-error-retry" onClick={() => (chunk ? window.location.reload() : this.setState({ error: null }))} className="btn-gold px-6 py-2.5 text-sm">{chunk ? "Reload" : "Try again"}</button>
            <a href="/" data-testid="route-error-home" className="btn-ghost-gold px-6 py-2.5 text-sm">Home</a>
          </div>
        </div>
      </div>
    );
  }
}

export function NetworkGuards() {
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    const img = (e) => {
      const el = e.target;
      if (el?.tagName === "IMG" && !el.dataset.fallback && el.src !== FALLBACK_IMG) { el.dataset.fallback = "1"; el.src = FALLBACK_IMG; }
    };
    const update = () => toast("New version available", { duration: Infinity, action: { label: "Reload", onClick: () => window.dispatchEvent(new Event("aetryx:apply-update")) } });
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("error", img, true);
    window.addEventListener("aetryx:update-ready", update);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); window.removeEventListener("error", img, true); window.removeEventListener("aetryx:update-ready", update); };
  }, []);
  if (!offline) return null;
  return (
    <div data-testid="offline-banner" role="status" className="fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-2 bg-[#2a1f05] py-2 text-xs font-semibold text-[#FFD86A]" style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}>
      <WifiOff className="h-3.5 w-3.5" /> You're offline — we'll reconnect automatically.
    </div>
  );
}
